/**
 * Task 진행 상태.
 * @description
 * - `PENDING` / `IN_PROGRESS` = 진행 중(active) → 폴링 대상.
 * - 나머지(`SUCCESS`/`PARTIAL_SUCCESS`/`FAILED`/`ERROR`/`CANCELLED`) = 완료(completed).
 */
export enum TaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  FAILED = 'FAILED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
}

/**
 * Task 단순화 상태(서버 계산값).
 * @description
 * - 서버가 raw `status`(7종)를 단순화 상태로 묶어 `statusType`으로 내려준다.
 *   - `PENDING`/`IN_PROGRESS` → `IN_PROGRESS`
 *   - `SUCCESS`/`PARTIAL_SUCCESS` → `SUCCESS`
 *   - `FAILED`/`ERROR`/`CANCELLED` → `FAILED`
 * - `WARNING` = 하나의 task가 벌크로 병렬 작업하다 일부만 실패한 부분 실패(예: 3 성공 / 2 실패).
 *   완료 상태이며 탭 그룹핑상 `FAILED`와 함께 '실패' 탭에 노출된다.
 * - 탭 그룹핑·상태 아이콘 분기는 모두 이 `statusType`을 단일 기준으로 사용한다.
 */
export enum TaskStatusType {
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  WARNING = 'WARNING',
  SKIPPED = 'SKIPPED',
}

/**
 * keyword 검색어 결합 연산자.
 */
export enum TaskQueryOperator {
  AND = 'and',
  OR = 'or',
}

/**
 * 진행 중(active) 상태 여부.
 * @description
 * - 서버 단순화 상태 `statusType`이 `IN_PROGRESS`인지로 판단한다.
 *   list 응답에 active task가 1개라도 있으면 3초 폴링을 켠다 (useTaskList).
 */
export const isActiveTaskStatus = (statusType: TaskStatusType): boolean =>
  statusType === TaskStatusType.IN_PROGRESS;

/**
 * 완료(completed) 상태 여부. `isActiveTaskStatus`의 여집합.
 * @description
 * - SUCCESS / FAILED 모두 완료로 간주(단건 dismiss 가능 상태).
 */
export const isCompletedTaskStatus = (statusType: TaskStatusType): boolean =>
  !isActiveTaskStatus(statusType);

/**
 * 태스크 매니저 팝업 탭 키.
 * @description
 * - 단일 list query를 FE에서 필터링하는 데 사용 (서버 status 필터 미사용).
 */
export type TaskTabKey = 'all' | 'in_progress' | 'success' | 'failed';

/**
 * 탭별 `statusType` 그룹핑.
 * @description
 * - in_progress / success 는 서버 단순화 상태 `statusType`과 1:1 대응한다.
 * - failed 탭은 완전 실패(`FAILED`)와 부분 실패(`WARNING`)를 함께 노출한다.
 */
export const matchesTaskTab = (tab: TaskTabKey, statusType: TaskStatusType | string): boolean => {
  switch (tab) {
    case 'all':
      return true;
    case 'in_progress':
      return statusType === TaskStatusType.IN_PROGRESS;
    case 'success':
      return statusType === TaskStatusType.SUCCESS;
    case 'failed':
      return statusType === TaskStatusType.FAILED || statusType === TaskStatusType.WARNING;
    default:
      return false;
  }
};

export interface TaskError {
  category: string;
  code: string;
  guidance?: string | null;
  message?: string | null;
  supportReference?: string | null;
}

/**
 * Task의 개별 step (resource 단위 진행 항목).
 * @description
 * - swagger: TaskStepModel. 상세("상세 보기")에서 펼쳐지는 하위 항목.
 */
export interface TaskStep {
  order: number;
  status: TaskStatus;
  statusType: TaskStatusType;
  stepKey: string;
  completedAt?: string | null;
  description?: string | null;
  endPercent?: number | null;
  error?: TaskError | null;
  message?: string | null;
  optional?: boolean;
  progressPercent?: number | null;
  resourceId?: string | null;
  resourceName?: string | null;
  startedAt?: string | null;
  startPercent?: number | null;
  steps?: TaskStep[] | null;
  title?: string;
}

/**
 * Task 집계 요약 (성공/실패 카운트).
 * @description
 * - swagger: TaskSummaryModel.
 */
export interface TaskSummary {
  failedCount?: number | null;
  skippedCount?: number | null;
  succeeded?: boolean | null;
  successCount?: number | null;
  totalCount?: number | null;
}

/**
 * 단일 Task 모델.
 * @description
 * - swagger: TaskModel. list/detail 공용. list 응답에서는 `steps`가 비어있을 수 있다.
 * - `progressPercent` / `totalCount` / `completedCount` / `expandable`는 서버 계산값(readOnly).
 */
export interface Task {
  completedCount: number;
  createdAt: string;
  expandable: boolean;
  id: string;
  progressPercent: number;
  projectId: string;
  requestId: string;
  serviceName: string;
  status: TaskStatus;
  statusType: TaskStatusType;
  taskType: string;
  title: string;
  totalCount: number;
  updatedAt: string;
  workflowId: string;
  actorUserId?: string | null;
  actorUserName?: string | null;
  completedAt?: string | null;
  currentStep?: string | null;
  description?: string | null;
  dismissedAt?: string | null;
  domainId?: string | null;
  error?: TaskError | null;
  link?: string | null;
  parentTaskId?: string | null;
  resourceId?: string | null;
  resourceName?: string | null;
  resourceType?: string | null;
  retryCount?: number;
  rootTaskId?: string | null;
  startedAt?: string | null;
  stepMessage?: string | null;
  steps?: TaskStep[];
  summary?: TaskSummary | null;
}

/**
 * Task list 조회 파라미터.
 * @description
 * - swagger: TaskQueryParamsModel. 전부 optional.
 * - 본 프로젝트는 페이지네이션 없이 `limit: -1`로 전체를 받아 FE에서 탭 필터링한다.
 */
export interface TaskListParams {
  actorUserId?: string;
  keyword?: string;
  keywordFields?: string;
  keywordOperator?: TaskQueryOperator;
  limit?: number;
  offset?: number;
  parentTaskId?: string;
  requestId?: string;
  resourceId?: string;
  resourceType?: string;
  rootTaskId?: string;
  searchOperator?: TaskQueryOperator;
  sorts?: string;
  status?: TaskStatus | string;
  taskType?: string;
  workflowId?: string;
}

/**
 * Task list 응답 (offset pagination).
 * @description
 * - swagger: PaginationModel[TaskModel].
 */
export interface TaskListResponse {
  data: Task[];
  hasMore: boolean;
  limit: number;
  nextOffset: number | null;
  offset: number;
  totalCount: number;
}

/**
 * 단일 Task 숨김 처리 응답.
 * @description
 * - swagger: TaskDismissResponseModel.
 */
export interface TaskDismissResponse {
  dismissedAt: string;
  id: string;
}

/**
 * 전체 Task 숨김 처리 응답.
 * @description
 * - swagger: TaskDismissAllResponseModel.
 */
export interface TaskDismissAllResponse {
  dismissedAt: string;
  dismissedCount: number;
}
